// ============================================
// Profile Page - New Style
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaUser, FaWallet, FaExchangeAlt, FaMoneyBillWave,
  FaHistory, FaHeadset, FaSignOutAlt, FaChevronRight,
  FaHome, FaChartBar, FaEye, FaFileAlt, FaIdCard, FaCheckCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import './Profile.css';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('none'); // none, pending, verified, rejected

  useEffect(() => {
    if (user) {
      if (user.isVerified) {
        setVerificationStatus('verified');
      } else if (user.verificationStatus === 'pending' || user.verificationStatus === 'rejected') {
        setVerificationStatus(user.verificationStatus);
      } else {
        setVerificationStatus('none');
      }
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success('ออกจากระบบสำเร็จ');
    navigate('/login');
  };

  const menuItems = [
    { title: 'ยืนยันตัวตน', icon: <FaIdCard />, link: '/verification', color: '#8b5cf6' },
    { title: 'ประวัติธุรกรรม', icon: <FaHistory />, link: '/history', color: '#06b6d4' },
    { title: 'ใบอนุญาต', icon: <FaFileAlt />, link: '/license', color: '#10b981' },
    { title: 'ติดต่อฝ่ายบริการ', icon: <FaHeadset />, link: '/support', color: '#ec4899' },
  ];

  return (
    <div className="profile-page">
      {/* Profile Header */}
      <div className="profile-header">
        <motion.div
          className="profile-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="profile-avatar">
            {user?.profileImage ? (
              <img src={user.profileImage} alt="Profile" />
            ) : (
              <FaUser />
            )}
          </div>
          
          <div className="profile-info">
            <div className="profile-name-wrapper">
              <h2 className="profile-name">{user?.username || 'User'}</h2>
              {verificationStatus === 'verified' && (
                <span className="verify-badge">
                  <FaCheckCircle />
                  <span>Verify</span>
                </span>
              )}
            </div>
            <p className="profile-id">ID: {user?.id}</p>
          </div>
        </motion.div>

        {/* Balance Card */}
        <motion.div
          className="balance-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <p className="balance-label">ยอดเงินคงเหลือ</p>
          <p className="balance-amount">${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="profile-content">
        <div className="quick-actions-row">
          <Link to="/deposit" className="quick-action-btn">
            <div className="action-icon deposit">
              <FaWallet />
            </div>
            <span>ฝากเงิน</span>
          </Link>
          <Link to="/transfer" className="quick-action-btn">
            <div className="action-icon transfer">
              <FaExchangeAlt />
            </div>
            <span>โอนเงิน</span>
          </Link>
          <Link to="/withdraw" className="quick-action-btn">
            <div className="action-icon withdraw">
              <FaMoneyBillWave />
            </div>
            <span>ถอนเงิน</span>
          </Link>
        </div>

        {/* Menu Items */}
        <div className="menu-section">
          {menuItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={item.link} className="menu-item">
                <div className="menu-icon" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
                  {item.icon}
                </div>
                <span className="menu-title">
                  {item.title}
                  {item.link === '/verification' && verificationStatus === 'pending' && ' (รอการตรวจสอบ)'}
                </span>
                <FaChevronRight className="menu-arrow" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Logout Button */}
        <button onClick={handleLogout} className="logout-btn">
          <FaSignOutAlt />
          <span>ออกจากระบบ</span>
        </button>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item active">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default Profile;
